import React from "react";
import styled, { keyframes } from "styled-components";
import { SubSection } from "./SubSection";
import { ActionBtn } from "./styles";

const BtnRow = styled.div`
    display: flex;
    gap: 8px;
`;

const sweep = keyframes`
    from { transform: translateX(-100%); }
    to   { transform: translateX(400%); }
`;

const ReadingRow = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const ReadingText = styled.div`
    font-size: 0.78rem;
    color: #6366f1;
    font-family: "Inter", system-ui, sans-serif;
`;

// The save that moved the property is still in flight, so the start and the end of the bar are real.
// Only its position in between is indeterminate, which is why it sweeps rather than fills.
const ReadingTrack = styled.div`
    height: 3px;
    border-radius: 2px;
    background: #e2e8f0;
    overflow: hidden;
`;

const ReadingSweep = styled.div`
    width: 25%;
    height: 100%;
    border-radius: 2px;
    background: #6366f1;
    animation: ${sweep} 1.1s ease-in-out infinite;
`;

// The pull can finish before the sweeping bar registers, so what it read stays on the page afterwards.
// It is not stored anywhere, so a reload clears it, which is fine - it only reports the last pull.
const Imported = styled.div`
    font-size: 0.78rem;
    color: #0f766e;
    background: #f0fdfa;
    border: 1px solid #99f6e4;
    border-radius: 6px;
    padding: 6px 10px;
    font-family: "Inter", system-ui, sans-serif;
`;

const Empty = styled.div`
    font-size: 0.85rem;
    color: #94a3b8;
    font-family: "Inter", system-ui, sans-serif;
`;

function busyText(busyWith, label) {
    if (busyWith === "removing") return `Removing stored ${label} data`;
    return `Importing the last 12 months from ${label}`;
}

// One row is one page on one day.
function importedText({ rows }, label) {
    if (rows === 0) return `No ${label} data for this property`;
    return `Imported ${rows.toLocaleString()} rows of ${label} data`;
}

// Every Google connection in workspace settings reads the same way: what is linked, then Connect or
// Change, then Remove once there is something to remove. Analytics and Search Console differ only in
// what they show as the details and what their dialog asks for.
export const Connection = ({ label, icon, isOwner, isConnected, busyWith, imported, emptyText, details, onEdit, onRemove, dialog }) => (
    <SubSection label={label} icon={icon}>
        {isConnected ? details : <Empty>{emptyText}</Empty>}
        {busyWith && (
            <ReadingRow>
                <ReadingText>{busyText(busyWith, label)}</ReadingText>
                <ReadingTrack><ReadingSweep /></ReadingTrack>
            </ReadingRow>
        )}
        {!busyWith && imported && (
            <Imported role="status">{importedText(imported, label)}</Imported>
        )}
        <BtnRow>
            <ActionBtn $secondary={isConnected} disabled={!isOwner || Boolean(busyWith)} onClick={onEdit}>{isConnected ? "Change" : "Connect"}</ActionBtn>
            {isConnected && (
                <ActionBtn $danger disabled={!isOwner || Boolean(busyWith)} onClick={onRemove}>Remove</ActionBtn>
            )}
        </BtnRow>
        {dialog}
    </SubSection>
);
